// i18n/index.mjs — LangProvider + useT()/useLang(). Default zh, switchable to en,
// persisted to localStorage. Written JSX-free (React.createElement) so it stays a
// valid .mjs module; the .jsx UI files consume the hooks it exports.
import {
  createElement, createContext, useContext, useState, useCallback, useEffect, useMemo,
} from 'react';
import zh from './zh.mjs';
import en from './en.mjs';

const DICT = { zh, en };
const SUPPORTED = ['zh', 'en'];
const DEFAULT_LANG = 'zh';
const STORAGE_KEY = 'dc_lang';

const LangContext = createContext(null);

function readInitialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED.includes(saved)) return saved;
  } catch {
    /* localStorage unavailable (SSR / privacy mode) — fall through to default */
  }
  return DEFAULT_LANG;
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);

  // Batch 43 (K1): the <html lang> attribute follows the ACTIVE language from
  // mount on — a returning en user used to keep lang="zh" (screen-reader voice,
  // CJK font selection) until the first manual toggle.
  useEffect(() => {
    try {
      document.documentElement.setAttribute('lang', lang);
    } catch {
      /* non-browser environment */
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === 'zh' ? 'en' : 'zh');
  }, [lang, setLang]);

  // t(key, vars?): active-language lookup, falling back to zh, then the raw key.
  // {name} placeholders in the string are interpolated from vars.
  const t = useCallback(
    (key, vars) => {
      const table = DICT[lang] || DICT[DEFAULT_LANG];
      let s = key in table ? table[key] : DICT[DEFAULT_LANG][key] ?? key;
      if (vars) {
        for (const k of Object.keys(vars)) {
          s = s.split('{' + k + '}').join(String(vars[k]));
        }
      }
      return s;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, toggleLang, t }), [lang, setLang, toggleLang, t]);
  return createElement(LangContext.Provider, { value }, children);
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within a LangProvider');
  return ctx;
}

export function useT() {
  return useLang().t;
}
